import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ArrowRightLeft, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { adminLogQueries } from '@/api/endpoints/admin-logs'
import { profileQueries } from '@/api/endpoints/profiles'
import { deleteRiotAccount, riotAccountQueries } from '@/api/endpoints/riot-accounts'
import { apiErrorMessage } from '@/api/errors'
import { ConfirmDialog } from '@/components/kit/confirm-dialog'
import { RoleGate } from '@/components/kit/role-gate'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { MoveAccountDialog } from '@/features/accounts/move-account-dialog'
import { AuditPanel } from '@/features/audit/audit-panel'
import { formatRank, formatRecord, isoDate } from '@/features/accounts/ranking-display'

export const Route = createFileRoute('/_authed/accounts/$uuid')({
  loader: ({ context, params }) =>
    context.queryClient.prefetchQuery(riotAccountQueries.detail(params.uuid)),
  component: AccountDetailPage,
})

function AccountDetailPage() {
  const { uuid } = Route.useParams()
  return <AccountDetail key={uuid} uuid={uuid} />
}

function AccountDetail({ uuid }: { uuid: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: account, isLoading, isError, error } = useQuery(riotAccountQueries.detail(uuid))
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [moving, setMoving] = useState(false)
  const playerUuid = account?.player?.uuid

  const remove = useMutation({
    mutationFn: () => deleteRiotAccount(uuid),
    onSuccess: async () => {
      toast.success('Riot account deleted')
      await (playerUuid
        ? navigate({ to: '/profiles/$uuid', params: { uuid: playerUuid } })
        : navigate({ to: '/profiles' }))
      queryClient.removeQueries({ queryKey: [...riotAccountQueries.all, 'detail', uuid] })
      queryClient.removeQueries({ queryKey: riotAccountQueries.summonerNames(uuid).queryKey })
      queryClient.removeQueries({ queryKey: riotAccountQueries.rankings(uuid).queryKey })
      queryClient.removeQueries({ queryKey: adminLogQueries.entity(uuid).queryKey })
      void queryClient.invalidateQueries({ queryKey: profileQueries.all })
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full max-w-3xl" />
      </div>
    )
  }

  if (isError || !account) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild>
          <Link to="/profiles">
            <ArrowLeft />
            Back to profiles
          </Link>
        </Button>
        <p role="alert" className="text-destructive">
          {apiErrorMessage(error ?? new Error('Riot account not found'))}
        </p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        {playerUuid ? (
          <Button variant="ghost" size="sm" asChild>
            <Link to="/profiles/$uuid" params={{ uuid: playerUuid }}>
              <ArrowLeft />
              {account.player?.name ?? 'Profile'}
            </Link>
          </Button>
        ) : (
          <Button variant="ghost" size="sm" asChild>
            <Link to="/profiles">
              <ArrowLeft />
              Profiles
            </Link>
          </Button>
        )}
        <h1 className="text-2xl font-semibold">{account.summoner_name || 'Riot account'}</h1>
        <RoleGate role="ROLE_SUPER_ADMIN">
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setMoving(true)}>
              <ArrowRightLeft />
              Move
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)}>
              <Trash2 />
              Delete
            </Button>
          </div>
        </RoleGate>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
          <CardDescription>Current Riot ID and verification</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Riot ID</dt>
            <dd>{account.summoner_name || '—'}</dd>
            <dt className="text-muted-foreground">Server</dt>
            <dd>{account.server ?? '—'}</dd>
            <dt className="text-muted-foreground">Verification</dt>
            <dd>{account.verification ?? '—'}</dd>
            <dt className="text-muted-foreground">Comment</dt>
            <dd>{account.verification_comment || '—'}</dd>
          </dl>
        </CardContent>
      </Card>

      <SummonerNamesCard uuid={uuid} />
      <RankingsCard uuid={uuid} />

      <RoleGate role="ROLE_SUPER_ADMIN">
        <AuditPanel entityUuid={uuid} description="Logged changes to this account" />
      </RoleGate>

      <RoleGate role="ROLE_SUPER_ADMIN">
        <MoveAccountDialog
          accountUuid={uuid}
          currentProfileUuid={playerUuid}
          open={moving}
          onOpenChange={setMoving}
        />
      </RoleGate>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this Riot account?"
        description="This permanently deletes its summoner names and ranking history. To keep them, move the account to another profile instead."
        confirmLabel="Delete"
        destructive
        onConfirm={() => remove.mutateAsync()}
      />
    </div>
  )
}

function SummonerNamesCard({ uuid }: { uuid: string }) {
  const { data, isLoading, isError, error } = useQuery(riotAccountQueries.summonerNames(uuid))
  return (
    <Card>
      <CardHeader>
        <CardTitle>Summoner names</CardTitle>
        <CardDescription>Every name this account has used</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : isError ? (
          <p role="alert" className="text-sm text-destructive">
            {apiErrorMessage(error)}
          </p>
        ) : !data?.length ? (
          <p className="text-sm text-muted-foreground">No names recorded.</p>
        ) : (
          <ul className="divide-y">
            {data.map((entry) => (
              <li
                key={`${entry.changed_at}-${entry.name}-${entry.tagline}`}
                className="flex justify-between gap-2 py-2 text-sm"
              >
                <span>{entry.tagline ? `${entry.name}#${entry.tagline}` : entry.name}</span>
                <time className="text-muted-foreground">{isoDate(entry.changed_at)}</time>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function RankingsCard({ uuid }: { uuid: string }) {
  const { data, isLoading, isError, error } = useQuery(riotAccountQueries.rankings(uuid))
  return (
    <Card>
      <CardHeader>
        <CardTitle>Ranking history</CardTitle>
        <CardDescription>Newest first</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : isError ? (
          <p role="alert" className="text-sm text-destructive">
            {apiErrorMessage(error)}
          </p>
        ) : !data?.length ? (
          <p className="text-sm text-muted-foreground">No rankings recorded.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Rank</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Winrate</TableHead>
                  <TableHead>Season</TableHead>
                  <TableHead>Best</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((ranking, index) => (
                  <TableRow key={`${ranking.created_at}-${index}`}>
                    <TableCell>{isoDate(ranking.created_at)}</TableCell>
                    <TableCell>{formatRank(ranking)}</TableCell>
                    <TableCell>{ranking.score}</TableCell>
                    <TableCell>{formatRecord(ranking)}</TableCell>
                    <TableCell>{ranking.season}</TableCell>
                    <TableCell>{ranking.best ? 'Yes' : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
